/* eslint-disable camelcase */
import axios from 'axios'
import pouchdb from '../pouchdb'
import i18n from '../i18n'
import config from '../configurations'
import { AES } from 'crypto-js'

const defaultData = {
  type: 'council_license',
  status: 'submitted',
  timestamp: new Date().toISOString(),
  business: {
    category: '',
    trading_no: ''
  },
  owner: { sameAsMail: false, mail_state: 'Sabah', state: 'Sabah', marital_status: 'single' }
}

const form = {
  namespaced: true,
  state: {
    categories: [],
    council: config.council,
    data: Object.assign({}, defaultData),
    error: null,
    items1: [],
    items2: [],
    items3: [],
    id: null,
    loading: false
  },
  getters: {
    isLoading: function (state, getters, rootState, rootGetters) {
      return state.loading
    }
  },
  mutations: {
    setCategories: (state, payload) => (state.categories = payload.categories),
    setError: function (state, payload = { error: null }) {
      state.error = payload.error
    },
    setLoading: function (state, payload = { loading: false }) {
      state.loading = payload.loading
    },
    setData: function (state, payload = { data: defaultData }) {
      state.data = payload.data
    },
    resetData: function (state) {
      state.data = Object.assign({}, defaultData)
    },
    patchApplicant: function (state, { applicant }) {
      state.data.applicant = Object.assign({}, state.data.applicant, applicant)
    },
    patchBusiness: function (state, { business }) {
      state.data.business = Object.assign({}, state.data.business, business)
    },
    patchBusinessActivity: function (state, { business_activity }) {
      if (state.data.business.business_activity.indexOf(business_activity) === -1) {
        state.data.business.business_activity.push(business_activity)
      }
    },
    removeBusinessActivity: function (state, { business_activity }) {
      for (var i = 0; i < state.data.business.business_activity.length; i++) {
        if (state.data.business.business_activity[i] === business_activity) {
          state.data.business.business_activity.splice(i, 1)
        }
      }
    },
    patchCouncilLicense: function (state, { council_license, index }) {
      state.data.council_license[index] = Object.assign({}, state.data.council_license[index], council_license)
    },
    patchOwner: function (state, { owner }) {
      state.data.owner = Object.assign({}, state.data.owner, owner)
    },
    patchManager: function (state, { manager }) {
      state.data.manager = Object.assign({}, state.data.manager, manager)
      delete state.data.manager.ownership_type
    },
    resetOwner: function (state) {
      state.data.owner = Object.assign({}, defaultData.owner)
    },
    resetManager: function (state) {
      state.data.manager = Object.assign({}, defaultData.manager)
    },
    setItems: function (state, { data }) {
      for (var i = 0; i < data.length; i++) {
        state.items1.push({ value: data[i].type, premise: data[i].premise })
        state.items2.push({ value: data[i].category, type: data[i].type, premise: data[i].premise })
        state.items3.push({ value: data[i].activity, type: data[i].category, premise: data[i].premise })
      }
    }
  },
  actions: {
    fetchData: async function (context, payload) {
      context.commit('setLoading', { loading: true })
      try {
        const decoded = atob(payload.id)
        const response = await pouchdb.council_operation.get(decoded)
        const [category, owner] = await Promise.all([
          pouchdb.council_operation.get(response.application.category_id),
          pouchdb.contact.get(response.application.applicant_id)
        ])
        response.category = category
        response.owner = owner
        context.commit('setData', { data: response })
        context.commit('setError', { error: null })
      } catch (error) {
        context.commit('setError', { error })
        this.dispatch('snackbar/setSnackBar', {
          color: 'error',
          message: i18n.t('fetching_data_failed'),
          visible: true
        })
        throw error
      } finally {
        context.commit('setLoading', { loading: false })
      }
    },
    fetchCategories: async function (context, payload) {
      context.commit('setLoading', { loading: true })
      try {
        const [responseCategories] = await Promise.all([
          pouchdb.council_operation.allDocs({
            startkey: `CLOC-`,
            endkey: `CLOC-\uffff`,
            include_docs: true
          })
        ])
        const categories = responseCategories.rows.length > 0 ? responseCategories.rows.map(row => row.doc) : []
        context.commit('setCategories', { categories })
        context.commit('setError', { error: null })
      } catch (error) {
        context.commit('setError', { error })
        this.dispatch('snackbar/setSnackBar', {
          color: 'error',
          message: i18n.t('fetching_data_failed'),
          visible: true
        })
        throw error
      } finally {
        context.commit('setLoading', { loading: false })
      }
    },
    resetData: async function (context, payload = {}) {
      context.commit('setData', { data: defaultData })
    },
    submitData: async function (context, payload = defaultData) {
      context.commit('setLoading', { loading: true })
      const data = Object.assign({}, context.state.data)
      const today = new Date()
      try {
        const owner = {
          '_id': null,
          'type': 'person',
          'name': {
            'full_name': data.owner.name.trim().toUpperCase(),
            'aliases': []
          },
          'identity': {
            'nric': null,
            'oldic': null,
            'passport': null
          },
          'phone': [{
            'type': 'mobile',
            'phone': data.owner.mobile_tel
          },
          {
            'type': 'office',
            'phone': data.owner.office_tel
          },
          {
            'type': 'home',
            'phone': data.owner.residential_tel
          }
          ],
          'email': [{
            'type': 'primary',
            'email': data.owner.email
          }],
          'address': [
            {
              'type': 'home',
              'address': data.owner.residential_address,
              'postcode': data.owner.postcode,
              'state': data.owner.state,
              'city': data.owner.city,
              'country': 'MY'
            },
            {
              'type': 'mail',
              'address': data.owner.mailing_address,
              'postcode': data.owner.mail_postcode,
              'state': data.owner.mail_state,
              'city': data.owner.mail_city,
              'country': 'MY'
            }
          ],
          'gender': data.owner.gender,
          'marital_status': data.owner.marital_status,
          'race': data.owner.race,
          'religion': data.owner.religion,
          'citizenship': data.owner.citizenship,
          'relationship': {},
          'remark': '',
          'status': 'active',
          'timestamp': today.toISOString(),
          'created_at': today.toISOString(),
          'author': 'eservices',
          'terminal_id': 'THTR-00000000'
        }
        owner.type = 'person'
        owner.identity[data.owner.identity_type] = data.owner.identity_no
        const totalResponse = await pouchdb.contact.allDocs({
          startkey: 'COCA-0000-\uffff',
          endkey: 'COCA-0000-',
          descending: true,
          limit: 1
        })
        const next_running =
          totalResponse.rows.length > 0
            ? parseInt(totalResponse.rows[0].id.split('-')[2]) + 1
            : 1
        const nextNumber = next_running.toString().padStart(8, '0')
        owner._id = `COCA-0000-${nextNumber}`
        const res = await pouchdb.contact.put(owner)
        const license = {
          '_id': null,
          'subcategoryid': '',
          'zone': '',
          'status': 'new',
          'quantity': '0',
          'register_date': '',
          'reference_file': '-',
          'no_license': '',
          'trading_id': '',
          'license_name': '',
          'license_ic': '',
          'charge_type': 'monthly',
          'default_license_fee': 0,
          'current_fee': 0,
          'arrears_fee': 0,
          'advance': 0,
          'notice': 0,
          'deposit': 0,
          'lawyer_cost': 0,
          'agreement_cost': 0,
          'timestamp': today.toISOString(),
          'created_at': today.toISOString(),
          'author': 'eservices',
          'terminal_id': 'THTR-00000000',
          'channel': 'eservices',
          'application': {
            'category_id': data.business.category,
            'category_name': context.state.categories.filter(row => row._id === data.business.category)[0].businesstype,
            'trading_no': data.business.trading_no.trim().toUpperCase(),
            'applicant_id': res.id,
            'applicant_name': data.owner.name.trim().toUpperCase()
          }
        }
        const totalLicense = await pouchdb.council_operation.allDocs({
          startkey: 'CLOLC-0000-\uffff',
          endkey: 'CLOLC-0000-',
          descending: true,
          limit: 1
        })
        const nextLicense =
          totalLicense.rows.length > 0
            ? parseInt(totalLicense.rows[0].id.split('-')[2]) + 1
            : 1
        const nextLicenseNo = nextLicense.toString().padStart(8, '0')
        license._id = `CLOLC-0000-${nextLicenseNo}`
        const response = await pouchdb.council_operation.put(license)
        context.commit('setData', { data: defaultData })
        context.commit('setError', { error: null })
        this.dispatch('snackbar/setSnackBar', {
          color: 'success',
          message: i18n.t('application_submitted'),
          visible: true
        })
        try {
          const body = {
            channel: 'sms',
            recipient: data.owner.mobile_tel,
            message: `Ref No: ${response.id}. Kami telah menerima permohonan anda. Sila kemukakan dokumen sokongan anda ke Bahagian Pelesenan dalam tempoh 14 hari bekerja.`,
            subject: 'MDKGU'
          }
          const key = config.AES_SECRET
          const ciphertext = AES.encrypt(JSON.stringify(body), key).toString()
          await axios.post('/api/notify', { message: btoa(ciphertext) }, {
            baseURL: 'https://sms.menggaris.com',
            withCredentials: false
          })
        } catch (error) {
          console.error(error)
        }
        return response
      } catch (error) {
        context.commit('setError', { error })
        if (error.response && error.response.data && error.response.data.error === 'conflict') {
          this.dispatch('snackbar/setSnackBar', {
            color: 'error',
            message: i18n.t('email_already_exist'),
            visible: true
          })
        } else {
          this.dispatch('snackbar/setSnackBar', {
            color: 'error',
            message: i18n.t('attempt_failed_please_try_again'),
            visible: true
          })
        }
        // throw error
      } finally {
        context.commit('setLoading', { loading: false })
      }
    }
  }
}

export default form
